import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../../dto/dto.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { ArgumentException } from '../../../exceptions/argument-exception.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../../utility/null-safety.js';
import { Dayjs } from 'dayjs';
import { TimeZone } from '../../../time/timezone.js';
import { TrueTime } from '../../../time/true-time.js';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const CODE_LENGTH = 5;
const FIRST_SHORT_CODE = 'AAAAA';

export { ALPHABET as SHORT_CODE_ALPHABET };
export { CODE_LENGTH as SHORT_CODE_LENGTH };

export class ShortCodePool extends DataModel {
  public static readonly TYPE_ID: string = '2782b5894a244a93af6ea0867352199c';

  private _state: number[];
  private _nextValue: string | null;

  private constructor(
    public readonly creationDate: Dayjs,
    value: string | null,
  ) {
    super();

    if(isNotNullOrUndefined(value) && value.length !== CODE_LENGTH) {
      throw new ArgumentException('value', `Start value must have exactly ${CODE_LENGTH} characters.`);
    }

    //  If [value] is null we consider the pool to be exhausted so we initialize with [ALPHABET.length - 1] by default.
    const state = Array<number>(CODE_LENGTH).fill(ALPHABET.length - 1);

    if(isNotNullOrUndefined(value)) {
      for(let i = 0; i < state.length; i++) {
        const index = ALPHABET.indexOf(value[i]);
        if(index < 0) {
          throw new ArgumentException('value', 'Start value must contain only characters from the alphabet.');
        }

        state[i] = index;
      }
    }

    this._nextValue = value;
    this._state = state;
  }

  get $typeId(): string {
    return ShortCodePool.TYPE_ID;
  }

  get isExhausted(): boolean {
    return this._nextValue == null;
  }

  // Returns the next short code if the pool is not exhausted, else returns [null]. Does not move the counter forward.
  // Calling [peek] multiple times will return the same value.
  peek(): string | null {
    return this._nextValue;
  }

  // Returns the next short code and moves the counter forward if the pool is not exhausted, else returns [null].
  next(): string | null {
    if(isNullOrUndefined(this._nextValue)) {
      return null;
    }

    for(let i = CODE_LENGTH - 1; i >= 0; i--) {
      this._state[i]++;
      if(this._state[i] < ALPHABET.length) {
        break;
      }

      this._state[i] = 0;
    }

    const next = this._state.map((e) => ALPHABET[e]).join('');
    const current = this._nextValue;

    // If we loop back(e.g. ZZZZZ -> AAAAA) we consider the pool to be exhausted otherwise
    // we would end up with duplicate short codes which is what we want to avoid.
    this._nextValue = next.localeCompare(current) > 0 ? next : null;

    return current;
  }

  static create(): ShortCodePool {
    return new ShortCodePool(TrueTime.now(), FIRST_SHORT_CODE);
  }

  static fromMap(context: SerializationContext, map: SerializedData): ShortCodePool {
    return new ShortCodePool(
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        MapUtils.get<string>(map, 'nextValue'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'creationDate': context.encodeDate(this.creationDate),
      'nextValue': this._nextValue,
    };
  }

  clone(): this {
    return new ShortCodePool(
        TimeZone.cloneDate(this.creationDate),
        this._nextValue
    ) as this;
  }

  isSame(other: this): boolean {
    if(this === other) {
      return true;
    }

    return this.creationDate == other.creationDate && this._nextValue === other._nextValue;
  }
}

export class ShortCodePoolBuilder extends DataModelBuilder<ShortCodePool> {
  build(context: SerializationContext, map: SerializedData): ShortCodePool {
    return ShortCodePool.fromMap(context, map);
  }
}
